import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BROWSE_RESULT_TYPES,
  BROWSE_SORTS,
  type BrowseResultType,
  type BrowseSort,
} from 'fym-shared/src/music24/browseSearch';
import { useSMLGenreParam } from '../../hooks/useSMLGenreParam';
import { useSMLCountryParam } from '../../hooks/useSMLCountryParam';

/** The URL keys of Browse (design a2 note). `genre` and `country` are the shared analytics keys. */
export const BROWSE_PARAMS = {
  query: 'q',
  type: 'type',
  sort: 'sort',
  service: 'service',
  hideFollowed: 'hideFollowed',
  genre: 'genre',
  country: 'country',
} as const;

export interface BrowseUrlState {
  query: string;
  type: BrowseResultType;
  sort: BrowseSort;
  genre: string | undefined;
  countryCode: string | undefined;
  service: string | undefined;
  hideFollowed: boolean;
}

export type BrowseUrlPatch = Partial<Record<keyof typeof BROWSE_PARAMS, string | undefined>>;

function pick<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((candidate) => candidate === value) ?? fallback;
}

/**
 * The search, the type tab and the filters live in the URL, so Back and a shared link keep them
 * (design a2 note). Every change replaces the history entry: a keystroke is not a page.
 */
export function useBrowseUrlState(): { state: BrowseUrlState; update: (patch: BrowseUrlPatch) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const { genre } = useSMLGenreParam();
  const { countryCode } = useSMLCountryParam();

  const state = useMemo<BrowseUrlState>(() => {
    const service = searchParams.get(BROWSE_PARAMS.service) ?? '';
    return {
      query: searchParams.get(BROWSE_PARAMS.query) ?? '',
      type: pick(searchParams.get(BROWSE_PARAMS.type), BROWSE_RESULT_TYPES, 'all'),
      sort: pick(searchParams.get(BROWSE_PARAMS.sort), BROWSE_SORTS, 'match'),
      genre,
      countryCode,
      service: service === '' ? undefined : service,
      hideFollowed: searchParams.get(BROWSE_PARAMS.hideFollowed) === '1',
    };
  }, [searchParams, genre, countryCode]);

  const update = useCallback(
    (patch: BrowseUrlPatch) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          (Object.keys(patch) as (keyof typeof BROWSE_PARAMS)[]).forEach((key) => {
            const value = patch[key];
            if (value === undefined || value === '') {
              next.delete(BROWSE_PARAMS[key]);
            } else {
              next.set(BROWSE_PARAMS[key], value);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { state, update };
}

/** True when a filter has a value: "Clear filters" shows only then (design a2 note). */
export function hasBrowseFilter(state: BrowseUrlState): boolean {
  return (
    state.genre !== undefined || state.countryCode !== undefined || state.service !== undefined || state.hideFollowed
  );
}
